import { RefObject } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { Divider, Stack, styled, useMediaQuery } from "@mui/material";
import { MenuBorderColor, TTSCustomEditorColors } from "shared/constants";
import { ttsCustomEditorStyles } from "styles/tts-custom-editor-styles";
import { ListOfVoices } from "@generated/list_of_voices";
// EditorActions
import UndoRedoMenu from "./ToolbarMenus/UndoRedoMenu";
import CopyAndClearMenu from "./ToolbarMenus/CopyAndClearMenu";
// SSML Tags
import SayAsMenu from "./ToolbarMenus/SayAsMenu";
import BreakMenu from "./ToolbarMenus/BreakMenu";
import EmphasisMenu from "./ToolbarMenus/EmphasisMenu";
import LanguageMenu from "./ToolbarMenus/LanguageMenu";
import VoiceMenu from "./ToolbarMenus/VoiceMenu";
import PronunciationMenu from "./ToolbarMenus/PronunciationMenu";
import SSMLToggleSwitch from "./components/SSMLToggleSwitch";
import ProsodyMenu from "./ToolbarMenus/ProsodyMenu";
import { useAtomValue } from "jotai";
import { isHtmlViewAtom } from "atoms";
import StyleMenu from "./ToolbarMenus/StyleMenu";

const getToolbarDivider = () => {
  return (
    <Divider
      orientation="vertical"
      sx={ttsCustomEditorStyles.toolbarMenuDivider}
    />
  );
};

interface CustomToolbarProps {
  editorRef: RefObject<EditorRef | null>;
  isSelectionInsideTag?: boolean;
  anyMenuDisabled?: boolean;
  handleToggleHtmlView: () => void;
  onClear: () => void;
  listOfVoices?: ListOfVoices;
  selectedVoice?: string;
}

export default function CustomToolbar({
  editorRef,
  isSelectionInsideTag = false,
  anyMenuDisabled = false,
  handleToggleHtmlView,
  onClear,
  listOfVoices,
  selectedVoice,
}: CustomToolbarProps): JSX.Element {
  const isHtmlView = useAtomValue(isHtmlViewAtom);
  const isVerySmallScreen = useMediaQuery("(max-width:800px)");
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  // Don't render toolbar on mobile screens (600px and below)
  if (isMobileScreen) {
    return <></>;
  }

  const ToolbarBackground = styled("div")(() => ({
    padding: "8px 12px",
    backgroundColor: TTSCustomEditorColors.ToolbarBackground,
    border: `1px solid ${
      isSelectionInsideTag || anyMenuDisabled
        ? "rgba(2, 136, 209, 1)"
        : MenuBorderColor
    }`,
    borderBottom: 0,
  }));

  return (
    <ToolbarBackground id="custom-tts-toolbar">
      <Stack
        direction="row"
        useFlexGap
        flexWrap={isVerySmallScreen ? "wrap" : "nowrap"}
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center">
          <SayAsMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <BreakMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <LanguageMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <EmphasisMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <ProsodyMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <StyleMenu
            editorRef={editorRef}
            listOfVoices={listOfVoices}
            selectedVoice={selectedVoice}
          />
          {getToolbarDivider()}
          <PronunciationMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <VoiceMenu editorRef={editorRef} />
        </Stack>
        <Stack direction="row" alignItems="center">
          <UndoRedoMenu editorRef={editorRef} />
          {getToolbarDivider()}
          <CopyAndClearMenu editorRef={editorRef} action="copy" />
          {getToolbarDivider()}
          <CopyAndClearMenu
            editorRef={editorRef}
            action="clear"
            onClear={onClear}
          />
          {getToolbarDivider()}
          <SSMLToggleSwitch
            isHtmlView={isHtmlView}
            handleToggleHtmlView={handleToggleHtmlView}
          />
        </Stack>
      </Stack>
    </ToolbarBackground>
  );
}
